import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '@web/i18n/locale.ts';
import {
  reportRetry,
  useReportDate,
  StatTile,
  ReportBoundary,
  SectionCard,
  CustomerLink,
  TH,
  THR,
  TD,
  TDR,
  ROW,
} from '../shared.tsx';

export function RetentionSection({ scope }: { scope?: 'all' }) {
  const formatDate = useReportDate();
  const { t } = useTranslation();
  const paging = useReportPaging({ scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'retention', { scope, pages }, paging.identity],
    queryFn: () => reportsApi.retention(scope, pages),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        const money = (cents: number) => formatCents(cents, report.currencyCode, getDateLocale());
        return (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              <StatTile
                label={t('reports.retention.active')}
                value={money(report.active.monthlyRunRateCents)}
                hint={t('reports.retention.activeHint', { count: report.active.series })}
              />
              <StatTile
                label={t('reports.retention.paused')}
                value={money(report.paused.monthlyRunRateCents)}
                hint={t('reports.retention.pausedHint', { count: report.paused.series })}
              />
              <StatTile
                label={t('reports.retention.ended')}
                value={money(report.endedInWindow.monthlyRunRateCents)}
                hint={t('reports.retention.endedHint', { count: report.endedInWindow.series })}
              />
            </div>

            <SectionCard
              title={t('reports.retention.winBackTitle')}
              collection="winBack"
              hint={t('reports.retention.winBackHint')}
              csvHref={reportsApi.csvUrl('retention', { scope })}
            >
              {report.winBack.length === 0 ? (
                <p className="text-[color:var(--color-muted)]">{t('reports.retention.winBackEmpty')}</p>
              ) : (
                <TableScroll aria-label={t('reports.retention.winBackTitle')} className="overflow-x-auto">
                  <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-[color:var(--color-line)]">
                        <th scope="col" className={TH}>
                          {t('reports.retention.colCustomer')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.retention.colLastVisit')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.retention.colVisits')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.retention.colAvgPrice')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.winBack.map((row) => (
                        <tr key={row.customerId} className={ROW}>
                          <td className={TD}>
                            <CustomerLink id={row.customerId} name={row.customerName} />
                          </td>
                          <td className={TDR}>{formatDate(row.lastVisitDate)}</td>
                          <td className={TDR}>{row.completedVisits}</td>
                          <td className={TDR}>{money(row.avgVisitPriceCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </SectionCard>
          </>
        );
      }}
    </ReportBoundary>
  );
}
